import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import axios from "axios";
import styled from "styled-components";

const Wrap = styled.span`
  display: inline-block;
  max-width: 100%;
  vertical-align: top;
  .email-reveal-button {
    font: inherit; color: inherit; text-decoration: underline;
    background: transparent; border: 0; padding: 0; cursor: pointer;
    text-align: left;
  }
  .email-reveal-button:focus-visible { outline: 2px solid #4f46e5; outline-offset: 4px; }
  .email-reveal-panel {
    display: block; background: #fff; color: #172033; padding: 16px;
    margin-top: 10px; border: 1px solid #cbd5e1; border-radius: 8px;
    font-size: 14px; line-height: 1.6; max-width: 100%;
  }
  .email-reveal-panel a { color: #3730a3; font-size: inherit; }
  .email-reveal-caption, .email-reveal-status { display: block; margin-bottom: 10px; }
  .email-reveal-panel .email-reveal-button { margin-top: 10px; }
`;

// The address is returned only after server-side CAPTCHA verification. It is
// deliberately absent from the static HTML, page data and JavaScript bundle.
export default function EmailReveal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [checking, setChecking] = useState(false);
  const captcha = useRef(null);
  const request = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => () => { request.current += 1; }, []);

  const close = () => {
    request.current += 1;
    inFlight.current = false;
    setOpen(false);
    setChecking(false);
    setStatus("");
  };

  const verify = async token => {
    if (!token || inFlight.current) return;
    inFlight.current = true;
    const current = ++request.current;
    setChecking(true);
    setStatus("Checking...");
    let responseInfo = {};
    try {
      const response = await axios.post(
        "https://members.hotsnail.com.au/api/PublicContact/RevealEmail",
        { token }, { timeout: 15000 }
      );
      if (request.current !== current) return;
      responseInfo = { status: response.status, dataType: typeof response.data,
        fields: response.data && typeof response.data === "object" ? Object.keys(response.data) : [] };
      if (!response.data || typeof response.data.email !== "string" ||
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(response.data.email)) throw new Error("Invalid response");
      setEmail(response.data.email);
      setOpen(false);
      setStatus("");
    } catch (error) {
      if (request.current !== current) return;
      console.warn("Email reveal verification failed", {
        message: error.message,
        status: error.response ? error.response.status : responseInfo.status,
        dataType: responseInfo.dataType, fields: responseInfo.fields,
      });
      setStatus("Please try the check again, or use our contact form.");
      if (captcha.current) captcha.current.reset();
    } finally {
      if (request.current === current) {
        inFlight.current = false;
        setChecking(false);
      }
    }
  };

  return <Wrap>
    {email ? <a href={`mailto:${email}`}>{email}</a> :
      <button className="email-reveal-button" type="button" aria-expanded={open}
        onClick={() => open ? close() : setOpen(true)}>Show email address</button>}
    {open && !email && <span className="email-reveal-panel" role="group" aria-label="Email verification" aria-busy={checking}>
      <span className="email-reveal-caption">Complete the quick check to see our email address.</span>
      <ReCAPTCHA ref={captcha} sitekey="6LfIDGwUAAAAAAW4Jy6wKQrKbJETEyCtCux6fnQ1"
        size="compact" onChange={verify}
        onExpired={() => setStatus("Please complete the check again.")}
        onErrored={() => setStatus("You can also reach us through our contact form.")} />
      <span className="email-reveal-status" role="status" aria-live="polite">{status}</span>
      <a href="/contact-us/">Use our contact form</a><br />
      <button className="email-reveal-button" type="button" onClick={close}>Close</button>
    </span>}
    <noscript><a href="/contact-us/">Contact HotSnail</a></noscript>
  </Wrap>;
}
